import { cn } from "@gettactic/helpers";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";
import { useWindowSize } from "@gettactic/helpers/src/hooks/useWindowSize";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";
import { Button, ButtonProps } from "../../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogFooterProps,
  DialogHeader
} from "../../../primitives/Dialog";

const MODAL_ANIMATION_DURATION = 200;

const modalSizeVariants = cva("", {
  variants: {
    size: {
      xs: "max-w-[28rem]",
      sm: "max-w-[32rem]",
      md: "max-w-[38rem]",
      lg: "max-w-[45rem]",
      xl: "max-w-[50rem]",
      "2xl": "max-w-[54rem]",
      "3xl": "max-w-[58rem]",
      "4xl": "max-w-[62rem]"
    }
  },
  defaultVariants: {
    size: "xl"
  }
});

export type ModalSize = NonNullable<
  VariantProps<typeof modalSizeVariants>["size"]
>;

export type ModalVariant = "default" | "kiosk" | "kioskDark";

const modalVariantStyles: Record<
  ModalVariant,
  {
    header: string;
    title: string;
    description: string;
    content: string;
    message: string;
    footer: string;
  }
> = {
  default: {
    header: "px-6 pt-6",
    title: "text font-bold text-2xl text-secondary-foreground",
    description: "mt-1 text-sm text-muted-foreground",
    content: "px-6 pb-6",
    message: "",
    footer: ""
  },
  kiosk: {
    header: "px-8 pt-8 pb-0 text-center sm:text-center",
    title: "font-bold text-3xl text-neutral-900",
    description: "mt-2 text-lg text-neutral-600",
    content: "px-8 pb-8 pt-2 text-center",
    message: "text-lg text-neutral-600",
    footer: "sm:justify-center"
  },
  kioskDark: {
    header: "px-8 pt-8 pb-0 text-center sm:text-center",
    title: "font-bold text-2xl text-white",
    description: "mt-2 text-base text-white/60",
    content: "px-8 pb-8 pt-2 text-center",
    message: "text-white/60",
    footer: "sm:justify-center"
  }
};

export interface ModalProps extends React.PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  title?: React.ReactNode;
  /** Description text displayed under the title. */
  description?: React.ReactNode;
  size?: ModalSize;
  /** Visual variant of the modal. Defaults to "default". */
  variant?: ModalVariant;
  closeOnOverlayClick?: boolean;
  isModal?: boolean;
  /**
   * Render the modal scoped inside {@link portalContainer} instead of the
   * viewport: the backdrop fills only that container and the modal centers
   * within it. Alternative behavior — leave unset for the default full-page
   * modal. The container must be a positioned element (e.g. `relative`).
   */
  contained?: boolean;
  /** DOM element to portal the modal into. Used with {@link contained}. */
  portalContainer?: HTMLElement | null;
  unmountContent?: boolean;
  contentClassName?: string;
  innerContentClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  /** Class name applied to the description. */
  descriptionClassName?: string;
  /** Class name applied to the message/children content area. */
  messageClassName?: string;
  /** Callback when cancel button is clicked. If provided, a footer with cancel button will be shown. */
  onCancel?: () => void;
  /** Label for the cancel button. Defaults to "Close". */
  cancelLabel?: React.ReactNode;
  /** Additional props for the cancel button. */
  cancelButtonProps?: Omit<ButtonProps, "onClick">;
  /** Hide the close button in the top-right corner. Defaults to false. */
  hideCloseButton?: boolean;
}

export interface ModalFooterProps extends DialogFooterProps {
  /** Visual variant of the footer. Defaults to "default". */
  variant?: ModalVariant;
}

export interface ModalComponent extends React.FC<ModalProps> {
  Body: React.FC<React.PropsWithChildren>;
  Footer: React.FC<ModalFooterProps>;
}

export const Modal: ModalComponent = ({
  children,
  isOpen,
  onClose,
  title,
  description,
  size,
  variant = "default",
  closeOnOverlayClick = true,
  onOpenChange,
  isModal,
  contained = false,
  portalContainer,
  unmountContent = false,
  innerContentClassName,
  contentClassName,
  headerClassName,
  titleClassName,
  descriptionClassName,
  messageClassName,
  onCancel,
  cancelLabel = "Close",
  cancelButtonProps,
  hideCloseButton = false
}) => {
  const browserWindowSize = useWindowSize();
  const browserHeight = browserWindowSize.height;
  const { isVisible } = useCloseAnimationTimeout(isOpen, {
    animationDuration: MODAL_ANIMATION_DURATION
  });

  const shouldRenderContent = unmountContent ? isVisible : true;
  const variantStyles = modalVariantStyles[variant];

  const handleOnClose = () => {
    if (onOpenChange) onOpenChange?.(false);
    else if (onClose) onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(isOpen) => {
        if (onOpenChange) onOpenChange(isOpen);
        else if (!isOpen) onClose();
      }}
      modal={isModal}
    >
      <DialogContent
        contained={contained}
        portalContainer={portalContainer}
        onClick={(e) => e.stopPropagation()}
        className={cn(modalSizeVariants({ size }), "p-0", contentClassName)}
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayProps={{
          onPointerDown: closeOnOverlayClick
            ? (e: React.PointerEvent) => {
                if (e.target === e.currentTarget) handleOnClose();
              }
            : undefined
        }}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        customCloseButton={hideCloseButton ? <></> : undefined}
        variant={variant}
      >
        <DialogHeader className={cn(variantStyles.header, headerClassName)}>
          {typeof title === "string" ? (
            <span className={cn(variantStyles.title, titleClassName)}>
              {title}
            </span>
          ) : (
            title || null
          )}
          {description && (
            <p className={cn(variantStyles.description, descriptionClassName)}>
              {description}
            </p>
          )}
        </DialogHeader>
        <div
          className={cn(
            variantStyles.content,
            modalSizeVariants({ size }),
            innerContentClassName
          )}
        >
          <div
            style={{
              maxHeight: browserHeight ? `${browserHeight - 200}px` : "32rem"
            }}
            className={cn(
              browserHeight && browserHeight < 700 ? "overflow-y-auto" : "",
              variantStyles.message,
              messageClassName
            )}
          >
            {shouldRenderContent && children}
          </div>
        </div>
        {onCancel && (
          <DialogFooter className="border-t py-4 px-6 border-t-neutral-100">
            <Button variant="outline" {...cancelButtonProps} onClick={onCancel}>
              {cancelLabel}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ModalBody: ModalComponent["Body"] = ({ children }) => {
  return <>{children}</>;
};

export const ModalFooter: ModalComponent["Footer"] = ({
  variant = "default",
  className,
  ...props
}) => {
  const variantStyles = modalVariantStyles[variant];
  return (
    <DialogFooter
      {...props}
      className={cn("mt-4 mb-1", variantStyles.footer, className)}
    />
  );
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
